import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  Chip,
  ImageList,
  ImageListItem,
  Divider,
  Switch,
  FormControlLabel,
  CircularProgress,
  Stack,
} from '@mui/material';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ImageIcon from '@mui/icons-material/Image';
import InfoIcon from '@mui/icons-material/Info';

import equipmentServices from '../../services/equipmentServices';
import equipmentTypesServices from '../../services/equipmentTypesServices';

/**
 * Equipment Select Modal
 * @param {boolean} open - Modal open state
 * @param {function} onClose - Close handler
 * @param {function} onSelect - Select handler (equipment data)
 * @param {string} systemId - System ID (for context)
 * @param {object} editingEquipment - If provided, modal works in edit mode
 *   - editingEquipment.type - Equipment type
 *   - editingEquipment.make - Manufacturer
 *   - editingEquipment.model - Model number
 *   - editingEquipment.price - Current price
 *   - editingEquipment.imageUrl - Current image URL
 * @param {boolean} isEditMode - Whether modal is in edit mode
 */
const EquipmentSelectModal = ({ open, onClose, onSelect, systemId, editingEquipment, isEditMode }) => {
  // Data
  const [equipmentFilters, setEquipmentFilters] = useState(null);
  const [equipmentTypes, setEquipmentTypes] = useState({});
  const [loading, setLoading] = useState(true);

  // Selection
  const [selectedType, setSelectedType] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  
  // Model data
  const [modelData, setModelData] = useState(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [addPicture, setAddPicture] = useState(true);
  const [customPrice, setCustomPrice] = useState('');

  // Load data
  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [filtersResponse, typesResponse] = await Promise.all([
        equipmentServices.getEquipmentKit(),
        equipmentTypesServices.getAll(),
      ]);

      if (filtersResponse.status === 200) {
        setEquipmentFilters(filtersResponse.data);
      }

      if (typesResponse.status === 200) {
        const typesMap = {};
        typesResponse.data.forEach(type => {
          typesMap[type.name] = { 
            imageUrl: type.imageUrl || null, 
            images: type.images || [] 
          };
        });
        setEquipmentTypes(typesMap);
      }
    } catch (error) {
      console.error('Error loading equipment data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize with editing data when in edit mode
  useEffect(() => {
    if (open && !loading && editingEquipment && isEditMode && equipmentFilters) {
      const { type, make, model, price, imageUrl } = editingEquipment;
      
      setSelectedType(type || '');
      setSelectedMake(make || '');
      setSelectedModel(model || '');
      setCustomPrice(price?.toString() || '');
      setSelectedImageUrl(imageUrl || null);
      setAddPicture(!!imageUrl);
      
      // Load model data
      if (type && make && model && equipmentFilters[type]?.[make]?.[model]) {
        const sizeData = equipmentFilters[type][make][model];
        const sizes = Object.keys(sizeData || {});
        const firstSizeData = sizes.length > 0 ? sizeData[sizes[0]] : {};
        
        setModelData({
          model,
          price: firstSizeData.price || price || 0,
          description: firstSizeData.description || '',
          imageUrl: firstSizeData.imageUrl || null,
        });
      }
    }
  }, [open, loading, editingEquipment, isEditMode, equipmentFilters]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedType('');
      setSelectedMake('');
      setSelectedModel('');
      setModelData(null);
      setSelectedImageUrl(null);
      setAddPicture(true);
      setCustomPrice('');
    }
  }, [open]);

  // Get available types
  const availableTypes = useMemo(() => {
    if (!equipmentFilters) return [];
    return Object.keys(equipmentFilters).sort();
  }, [equipmentFilters]);

  // Get available makes for selected type
  const availableMakes = useMemo(() => {
    if (!equipmentFilters || !selectedType) return [];
    return Object.keys(equipmentFilters[selectedType] || {}).sort();
  }, [equipmentFilters, selectedType]);

  // Get available models for selected type and make
  const availableModels = useMemo(() => {
    if (!equipmentFilters || !selectedType || !selectedMake) return [];
    return Object.keys(equipmentFilters[selectedType]?.[selectedMake] || {}).sort();
  }, [equipmentFilters, selectedType, selectedMake]);

  // Handle type change
  const handleTypeChange = (type) => {
    setSelectedType(type);
    setSelectedMake('');
    setSelectedModel('');
    setModelData(null);
    setSelectedImageUrl(null);
    setCustomPrice('');
  };

  // Handle make change
  const handleMakeChange = (make) => {
    setSelectedMake(make);
    setSelectedModel('');
    setModelData(null);
    setSelectedImageUrl(null);
    setCustomPrice('');
  };

  // Handle model change
  const handleModelChange = (model) => {
    setSelectedModel(model);
    
    if (model && equipmentFilters && selectedType && selectedMake) {
      const sizeData = equipmentFilters[selectedType][selectedMake][model];
      const sizes = Object.keys(sizeData || {});
      const firstSizeData = sizes.length > 0 ? sizeData[sizes[0]] : {};
      
      const data = {
        model,
        price: firstSizeData.price || 0,
        description: firstSizeData.description || '',
        imageUrl: firstSizeData.imageUrl || null,
        refrigerant: firstSizeData.refrigerant || null,
      };
      
      setModelData(data);
      setCustomPrice(data.price?.toString() || '');
      
      // Set default image
      const defaultImage = data.imageUrl || equipmentTypes[selectedType]?.imageUrl || null;
      setSelectedImageUrl(defaultImage);
    } else {
      setModelData(null);
      setSelectedImageUrl(null);
      setCustomPrice('');
    }
  };

  // Get available images for selected model
  const availableImages = useMemo(() => {
    if (!selectedModel || !selectedType) return [];
    
    const images = [];
    
    // Model's own image
    if (modelData?.imageUrl) {
      images.push(modelData.imageUrl);
    }
    
    // Type's images
    const typeImages = equipmentTypes[selectedType]?.images || [];
    typeImages.forEach(img => {
      if (!images.includes(img)) {
        images.push(img);
      }
    });
    
    // Type's default image
    const typeDefault = equipmentTypes[selectedType]?.imageUrl;
    if (typeDefault && !images.includes(typeDefault)) {
      images.push(typeDefault);
    }
    
    // Include current image if editing and not in list
    if (isEditMode && editingEquipment?.imageUrl && !images.includes(editingEquipment.imageUrl)) {
      images.unshift(editingEquipment.imageUrl);
    }
    
    return images;
  }, [selectedModel, selectedType, modelData, equipmentTypes, isEditMode, editingEquipment]);

  // Handle add/update equipment
  const handleAdd = () => {
    if (!selectedModel || !selectedType || !selectedMake) return;

    const finalImageUrl = addPicture ? selectedImageUrl : null;
    
    onSelect({
      type: selectedType,
      make: selectedMake,
      model: selectedModel,
      price: parseFloat(customPrice) || 0,
      imageUrl: finalImageUrl,
      description: modelData?.description || '',
      refrigerant: modelData?.refrigerant || null,
    });
    
    onClose();
  };

  const dialogTitle = isEditMode ? 'Edit Equipment' : 'Select Equipment';
  const buttonText = isEditMode ? 'Update Equipment' : 'Add Equipment';

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 1 }}>
            {/* Selectors Row */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              {/* Type */}
              <FormControl fullWidth>
                <InputLabel>Type *</InputLabel>
                <Select
                  value={selectedType}
                  label="Type *"
                  onChange={(e) => handleTypeChange(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select Type</em>
                  </MenuItem>
                  {availableTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Manufacturer */}
              <FormControl fullWidth disabled={!selectedType}>
                <InputLabel>Manufacturer *</InputLabel>
                <Select
                  value={selectedMake}
                  label="Manufacturer *"
                  onChange={(e) => handleMakeChange(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select Manufacturer</em>
                  </MenuItem>
                  {availableMakes.map((make) => (
                    <MenuItem key={make} value={make}>{make}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Model */}
              <FormControl fullWidth disabled={!selectedMake}>
                <InputLabel>Model *</InputLabel>
                <Select
                  value={selectedModel}
                  label="Model *"
                  onChange={(e) => handleModelChange(e.target.value)}
                  renderValue={(val) => val || ''}
                >
                  <MenuItem value="">
                    <em>Select Model</em>
                  </MenuItem>
                  {availableModels.map((model) => {
                    const sizeData = equipmentFilters?.[selectedType]?.[selectedMake]?.[model];
                    const firstSize = sizeData ? Object.values(sizeData)[0] : null;
                    const price = firstSize?.price ? Number(firstSize.price) : 0;
                    return (
                      <MenuItem key={model} value={model} sx={{ whiteSpace: 'normal', display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <span>{model}</span>
                        {price > 0 && (
                          <span style={{ color: '#4caf50', fontWeight: 600, fontSize: 13, flexShrink: 0 }}>
                            ${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        )}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Stack>

            {/* Model Details */}
            {selectedModel && modelData ? (
              <Paper variant="outlined" sx={{ p: 3 }}>
                {/* Header */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                    <Chip label={selectedType} color="primary" size="small" />
                    <Chip label={selectedMake} variant="outlined" size="small" />
                  </Box>
                  <Typography variant="h6" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                    {selectedModel}
                  </Typography>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Description */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <InfoIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Description
                    </Typography>
                  </Box>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      bgcolor: 'grey.50', 
                      minHeight: 120,
                      maxHeight: 200,
                      overflow: 'auto',
                    }}
                  >
                    {modelData.description ? (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                        {modelData.description}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        No description available for this model
                      </Typography>
                    )}
                  </Paper>
                </Box>

                {/* Price and Image Settings */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                  {/* Price */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                      Price
                    </Typography>
                    <TextField
                      value={customPrice}
                      onChange={(e) => setCustomPrice(e.target.value)}
                      type="number"
                      size="small"
                      fullWidth
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  </Box>

                  {/* Image Toggle */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                      Image
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={addPicture}
                          onChange={(e) => setAddPicture(e.target.checked)}
                        />
                      }
                      label="Include image in proposal"
                    />
                  </Box>
                </Stack>

                {/* Image Selection */}
                {addPicture && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                      Select Image {availableImages.length > 0 && `(${availableImages.length} available)`}
                    </Typography>
                    
                    {availableImages.length > 0 ? (
                      <ImageList cols={5} gap={12} sx={{ m: 0 }}>
                        {availableImages.map((img, idx) => (
                          <ImageListItem 
                            key={idx}
                            onClick={() => setSelectedImageUrl(img)}
                            sx={{ 
                              cursor: 'pointer',
                              border: selectedImageUrl === img ? '3px solid' : '2px solid',
                              borderColor: selectedImageUrl === img ? 'primary.main' : 'divider',
                              borderRadius: 1,
                              overflow: 'hidden',
                              position: 'relative',
                              '&:hover': { borderColor: 'primary.light' },
                            }}
                          >
                            <img
                              src={img}
                              alt={`Option ${idx + 1}`}
                              loading="lazy"
                              style={{ 
                                height: 80, 
                                objectFit: 'contain', 
                                backgroundColor: '#f5f5f5' 
                              }}
                            />
                            {selectedImageUrl === img && (
                              <CheckCircleIcon 
                                color="primary" 
                                sx={{ 
                                  position: 'absolute', 
                                  top: 4, 
                                  right: 4, 
                                  fontSize: 20,
                                  bgcolor: 'white',
                                  borderRadius: '50%',
                                }} 
                              />
                            )}
                          </ImageListItem>
                        ))}
                      </ImageList>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary', bgcolor: 'grey.50', borderRadius: 1 }}>
                        <ImageIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                        <Typography variant="body2">No images available for this equipment type</Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            ) : (
              /* Empty State */
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 4, 
                  textAlign: 'center', 
                  color: 'text.secondary',
                  bgcolor: 'grey.50',
                }}
              >
                <InfoIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
                <Typography variant="h6" gutterBottom>
                  {isEditMode ? 'Select new equipment' : 'Select equipment to see details'}
                </Typography>
                <Typography variant="body2">
                  Choose Type → Manufacturer → Model to view description and available images
                </Typography>
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleAdd} 
          variant="contained" 
          disabled={!selectedModel}
        >
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EquipmentSelectModal;
